import { supabase } from '../lib/supabase';
import { isDemoModeActive } from '../contexts/DemoContext';
import type {
  Team,
  TeamMember,
  TeamInvitation,
  SendInvitationRequest,
  SendInvitationResponse,
  RespondInvitationRequest,
  RespondInvitationResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  UpdateMemberRoleRequest,
} from '../types/team';

// SupabaseクライアントからURLを動的に取得
const getFunctionsUrl = () => {
  // @ts-ignore - supabaseClientの内部プロパティにアクセス
  return supabase.supabaseUrl || '';
};

const FUNCTIONS_URL = getFunctionsUrl();

/**
 * チーム一覧を取得（RPC関数を使用してセキュアにアクセス）
 */
export const getTeams = async (): Promise<Team[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .rpc('get_user_teams', { user_id_param: user.id });

  if (error) {
    console.error('Error fetching teams:', error);
    throw error;
  }

  // RPC関数の戻り値をTeam型に変換
  return (data || []).map((row: any) => ({
    id: row.team_id,
    name: row.team_name,
    description: row.team_description,
    owner_id: row.team_owner_id,
    created_at: row.team_created_at,
    updated_at: row.team_updated_at,
  }));
};

/**
 * チーム詳細を取得
 */
export const getTeam = async (teamId: string): Promise<Team | null> => {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single();

  if (error) {
    console.error('Error fetching team:', error);
    throw error;
  }

  return data;
};

/**
 * チームを作成
 */
export const createTeam = async (request: CreateTeamRequest): Promise<Team> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // RPC関数を使用してチームを作成（RLSをバイパス）
  const { data: teams, error: teamError } = await supabase
    .rpc('create_team_via_rpc', {
      team_name: request.name,
      team_description: request.description || null,
    });

  if (teamError) {
    console.error('Error creating team via RPC:', teamError);
    throw teamError;
  }

  if (!teams || teams.length === 0) {
    throw new Error('No team returned from RPC function');
  }

  // オーナーは team_owner_auto_add トリガーで自動的に team_members に追加される
  // フロントエンドでの手動追加は不要（重複エラーを防ぐ）

  return teams[0];
};

/**
 * チームを更新
 */
export const updateTeam = async (teamId: string, request: UpdateTeamRequest): Promise<Team> => {
  const { data, error } = await supabase
    .from('teams')
    .update({
      name: request.name,
      description: request.description,
      updated_at: new Date().toISOString(),
    })
    .eq('id', teamId)
    .select()
    .single();

  if (error) {
    console.error('Error updating team:', error);
    throw error;
  }

  return data;
};

/**
 * チームを削除
 */
export const deleteTeam = async (teamId: string): Promise<void> => {
  // チームに紐付いているプロジェクトを確認
  const projects = await getTeamProjects(teamId);

  if (projects.length > 0) {
    throw new Error(`このチームには${projects.length}個のプロジェクトが紐付いています。先にプロジェクトのチーム設定を解除してください。`);
  }

  const { error } = await supabase
    .from('teams')
    .delete()
    .eq('id', teamId);

  if (error) {
    console.error('Error deleting team:', error);
    throw error;
  }
};

/**
 * チームメンバー一覧を取得（RPC関数を使用してセキュアにアクセス）
 */
export const getTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .rpc('get_team_members', {
      team_id_param: teamId,
      requesting_user_id: user.id
    });

  if (error) {
    console.error('Error fetching team members:', error);
    throw error;
  }

  // RPC関数の戻り値をTeamMember型に変換
  return (data || []).map((row: any) => ({
    id: row.member_id,
    team_id: teamId,
    user_id: row.user_id,
    role: row.role,
    joined_at: row.joined_at,
    email: row.email || '',
    nickname: row.nickname || 'Unknown',
    avatar_url: null,  // RPC関数では avatar_url を返していない
  }));
};

/**
 * チームメンバーの権限を更新（RPC関数を使用）
 */
export const updateMemberRole = async (
  teamId: string,
  request: UpdateMemberRoleRequest
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .rpc('update_team_member_role', {
      member_id_param: request.memberId,
      team_id_param: teamId,
      new_role_param: request.role,
      requesting_user_id: user.id
    });

  if (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

/**
 * チームメンバーを削除（RPC関数を使用）
 */
export const removeMember = async (teamId: string, memberId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .rpc('remove_team_member', {
      member_id_param: memberId,
      team_id_param: teamId,
      requesting_user_id: user.id
    });

  if (error) {
    console.error('Error removing member:', error);
    throw error;
  }
};

/**
 * チームから退出（RPC関数を使用）
 */
export const leaveTeam = async (teamId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .rpc('leave_team', {
      team_id_param: teamId,
      user_id_param: user.id
    });

  if (error) {
    console.error('Error leaving team:', error);
    throw error;
  }
};

/**
 * チーム招待を送信（Edge Function使用）
 */
export const sendInvitation = async (
  request: SendInvitationRequest
): Promise<SendInvitationResponse> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('User not authenticated');

  const url = `${FUNCTIONS_URL}/functions/v1/send-team-invitation`;
  console.log('Sending invitation to:', url);
  console.log('FUNCTIONS_URL:', FUNCTIONS_URL);
  console.log('Request:', request);
  console.log('User ID:', session.user?.id);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  console.log('Response status:', response.status);

  if (!response.ok) {
    const error = await response.json();
    console.error('Error response:', error);
    throw new Error(error.error || 'Failed to send invitation');
  }

  return await response.json();
};

/**
 * チーム招待に応答（Edge Function使用）
 */
export const respondToInvitation = async (
  request: RespondInvitationRequest
): Promise<RespondInvitationResponse> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('User not authenticated');

  const response = await fetch(`${FUNCTIONS_URL}/functions/v1/respond-team-invitation`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to respond to invitation');
  }

  return await response.json();
};

/**
 * チーム招待一覧を取得
 */
export const getTeamInvitations = async (teamId: string): Promise<TeamInvitation[]> => {
  const { data, error } = await supabase
    .from('team_invitations')
    .select('*')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching team invitations:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    return [];
  }

  // 招待履歴からニックネーム情報を取得
  const invitationsWithNicknames = await Promise.all(
    data.map(async (invitation) => {
      const { data: historyData } = await supabase
        .from('team_invitation_history')
        .select('invitee_nickname')
        .eq('invitee_email', invitation.email)
        .eq('team_id', teamId)
        .maybeSingle();

      return {
        ...invitation,
        nickname: historyData?.invitee_nickname || null,
      };
    })
  );

  return invitationsWithNicknames;
};

/**
 * 自分宛の招待一覧を取得
 */
export const getMyInvitations = async (): Promise<TeamInvitation[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.email) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('team_invitations')
    .select(`
      *,
      teams (
        name
      )
    `)
    .eq('email', user.email)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching my invitations:', error);
    throw error;
  }

  // team_nameを追加
  return (data || []).map(inv => ({
    ...inv,
    team_name: inv.teams?.name,
  }));
};

/**
 * チーム招待を削除
 */
export const deleteInvitation = async (invitationId: string): Promise<void> => {
  const { error } = await supabase
    .from('team_invitations')
    .delete()
    .eq('id', invitationId);

  if (error) {
    console.error('Error deleting invitation:', error);
    throw error;
  }
};


/**
 * プロジェクトをチームに紐付け
 */
export const addProjectToTeam = async (projectId: string, teamId: string): Promise<void> => {
  // プロジェクトをチームプロジェクトとして設定
  const { error: updateError } = await supabase
    .from('projects')
    .update({
      is_team_project: true,
      team_id: teamId,
    })
    .eq('id', projectId);

  if (updateError) {
    console.error('Error updating project:', updateError);
    throw updateError;
  }

  // project_teamsテーブルに関連を追加
  const { error: linkError } = await supabase
    .from('project_teams')
    .insert({
      project_id: projectId,
      team_id: teamId,
    });

  if (linkError) {
    console.error('Error linking project to team:', linkError);
    throw linkError;
  }
};

/**
 * プロジェクトとチームの紐付けを解除
 */
export const removeProjectFromTeam = async (projectId: string): Promise<void> => {
  // project_teamsテーブルから削除
  const { error: unlinkError } = await supabase
    .from('project_teams')
    .delete()
    .eq('project_id', projectId);

  if (unlinkError) {
    console.error('Error unlinking project from team:', unlinkError);
    throw unlinkError;
  }

  // プロジェクトのタスクから担当者と進捗をクリア
  const { error: clearTodosError } = await supabase
    .from('todos')
    .update({
      assigned_to_user_id: null,
      progress_percentage: 0,
    })
    .eq('project_id', projectId);

  if (clearTodosError) {
    console.error('Error clearing todos assignment:', clearTodosError);
    // エラーが出てもプロジェクトは個人化を続ける
  }

  // プロジェクトを個人プロジェクトに戻す
  const { error: updateError } = await supabase
    .from('projects')
    .update({
      is_team_project: false,
      team_id: null,
    })
    .eq('id', projectId);

  if (updateError) {
    console.error('Error updating project:', updateError);
    throw updateError;
  }
};

/**
 * チームのプロジェクト一覧を取得
 */
export const getTeamProjects = async (teamId: string) => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching team projects:', error);
    throw error;
  }

  return data || [];
};

// =============================================
// team_members用のRPC関数ヘルパー（セキュア）
// =============================================

/**
 * チームメンバー一覧を取得（ユーザープロファイル付き）
 */
export const getTeamMembersWithProfiles = async (teamId: string): Promise<TeamMember[]> => {
  const { data, error } = await supabase
    .rpc('get_team_members_with_profiles', { team_id_param: teamId });

  if (error) {
    console.error('Error fetching team members:', error);
    throw error;
  }

  return data || [];
};

/**
 * ユーザーのチームロールを取得
 */
export const getUserTeamRole = async (teamId: string, userId: string): Promise<string | null> => {
  // デモモード中はDB呼び出しをスキップ
  if (isDemoModeActive()) {
    return 'member';  // デモ用のデフォルト値
  }

  const { data, error } = await supabase
    .rpc('get_user_team_role', {
      team_id_param: teamId,
      user_id_param: userId
    });

  if (error) {
    console.error('Error fetching user team role:', error);
    return null;
  }

  return data;
};

/**
 * チームメンバー数を取得
 */
export const getTeamMemberCount = async (teamId: string): Promise<number> => {
  // デモモード中はDB呼び出しをスキップ
  if (isDemoModeActive()) {
    return 3;  // デモ用のデフォルト値
  }

  const { data, error } = await supabase
    .rpc('get_team_member_count', { team_id_param: teamId });

  if (error) {
    console.error('Error fetching team member count:', error);
    return 0;
  }

  return data || 0;
};

/**
 * ユーザーの所属チーム一覧を取得
 */
export const getUserTeamMemberships = async (userId: string): Promise<{team_id: string, team_name: string}[]> => {
  const { data, error } = await supabase
    .rpc('get_user_team_memberships', { user_id_param: userId });

  if (error) {
    console.error('Error fetching user team memberships:', error);
    throw error;
  }

  return data || [];
};

/**
 * 招待を受諾（メンバー追加）
 */
export const acceptTeamInvitationViaRPC = async (
  invitationToken: string
): Promise<{team_id: string, team_name: string, user_role: string, inviter_id: string | null}> => {
  const { data, error } = await supabase
    .rpc('accept_team_invitation', { invitation_token_param: invitationToken });

  if (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    throw new Error('No data returned from invitation acceptance');
  }

  return data[0];
};

/**
 * ユーザーがチームメンバーかチェック
 */
export const checkTeamMembership = async (teamId: string, userId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .rpc('check_team_membership', {
      team_id_param: teamId,
      user_id_param: userId
    });

  if (error) {
    console.error('Error checking team membership:', error);
    return false;
  }

  return data || false;
};
